/**
 * GenerateInvitation.tsx
 *
 * 4-step invitation wizard.
 *
 * Steps: City → Theme → Details → Image Builder
 *
 * The Image Builder step replaces the old Preview + Generate flow with an
 * interactive per-design image generation experience. Users can edit prompts,
 * add reference images, and generate/regenerate each invitation variant.
 *
 * The DashboardLayout header receives a `LinearTimeline` via `setHeaderTitle`
 * (same pattern as GeneratePresentation / WizardTimeline). This requires the
 * route to be listed in `FULL_BLEED_PATTERNS` in `constants/routes.ts` so
 * the page gets the full-bleed content area.
 */

import { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { useInvitationEditorStore } from '@/stores/useInvitationEditorStore';
import { Spinner } from '@/components/ui';
import { SelectCity } from '@/pages/Dashboard/GeneratePresentation/steps';
import { SelectThemeStep } from '@/components/themes';
import { LinearTimeline } from '@/components/wizard';
import { EventDetailsStep, InvitationImageStep } from './steps';
import type { PresentationItem } from '@/types/presentation';
import type { InvitationWizardFormData, InvitationWizardStep } from '@/types/wizard';
import styles from './GenerateInvitation.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_FORM: InvitationWizardFormData = {
  city: '',
  theme_slugs: [],
  event_name: '',
  event_type: 'Corporate',
  event_date: '',
  time: '',
  venue: '',
  dress_code: '',
  special_notes: '',
  logo_url: '',
  reference_images: [],
  approved_brief: '',
};

const INDICATOR_STEPS: InvitationWizardStep[] = ['city', 'theme', 'details', 'images'];
const STEP_LABELS = ['City', 'Theme', 'Details', 'Build'];

// ─── Layout context type ──────────────────────────────────────────────────────

type LayoutContext = {
  setHeaderSlot: (node: React.ReactNode) => void;
  setHeaderTitle: (node: React.ReactNode) => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GenerateInvitation — 5-step wizard for AI-powered invitation generation.
 *
 * State is managed with local `useState` (no Zustand store for wizard flow).
 * The Zustand `useInvitationEditorStore` is used only to prime the editor with
 * existing invitation data when in edit mode.
 */
export const GenerateInvitation = () => {
  const { id } = useParams<{ id?: string }>();
  const store = useInvitationEditorStore();
  const { setHeaderTitle } = useOutletContext<LayoutContext>();

  const isEditMode = Boolean(id);
  const [step, setStep] = useState<InvitationWizardStep>(isEditMode ? 'details' : 'city');
  const [formData, setFormData] = useState<InvitationWizardFormData>(INITIAL_FORM);

  const currentStepIndex = INDICATOR_STEPS.indexOf(step);

  // ── Form helper ───────────────────────────────────────────────────────────

  const updateForm = <K extends keyof InvitationWizardFormData>(
    key: K,
    value: InvitationWizardFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ── Inject LinearTimeline into DashboardLayout header ─────────────────────

  useEffect(() => {
    setHeaderTitle(
      <LinearTimeline
        steps={STEP_LABELS}
        currentStep={currentStepIndex >= 0 ? currentStepIndex : STEP_LABELS.length - 1}
        onStepClick={(stepIdx) => {
          if (stepIdx < currentStepIndex) {
            setStep(INDICATOR_STEPS[stepIdx]);
          }
        }}
      />,
    );
    return () => setHeaderTitle(null);
  }, [currentStepIndex, setHeaderTitle]);

  // ── Load existing invitation in edit mode ────────────────────────────────

  const { data: existingItem, isLoading: isLoadingExisting } = useQuery({
    queryKey: ['inv-editor-load', id],
    queryFn: async () => {
      const res = await api.get<PresentationItem>(`${API_ROUTES.presentations}${id}/`);
      return res.data;
    },
    enabled: isEditMode,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (existingItem && store.editingId !== existingItem.id) {
      store.loadFromExisting(existingItem);
      const ctx = (existingItem.context_data ?? {}) as Record<string, unknown>;
      setFormData({
        city: typeof ctx.city === 'string' ? ctx.city : '',
        theme_slugs: Array.isArray(ctx.theme_slugs)
          ? (ctx.theme_slugs as string[])
          : typeof ctx.theme_slug === 'string' && ctx.theme_slug
            ? [ctx.theme_slug as string]
            : [],
        event_name: existingItem.name,
        event_type: typeof ctx.eventType === 'string' ? ctx.eventType : 'Corporate',
        event_date: typeof ctx.eventDate === 'string' ? ctx.eventDate : '',
        time: typeof ctx.eventTime === 'string' ? ctx.eventTime : '',
        venue: typeof ctx.location === 'string' ? ctx.location : '',
        dress_code: typeof ctx.dressCode === 'string' ? ctx.dressCode : '',
        special_notes: typeof ctx.specialNotes === 'string' ? ctx.specialNotes : '',
        logo_url: typeof ctx.logo_url === 'string' ? ctx.logo_url : '',
        reference_images: [],
        approved_brief: '',
      });
      setStep('details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingItem]);

  // ── Reset on new-mode mount ───────────────────────────────────────────────

  useEffect(() => {
    if (!isEditMode) {
      store.reset();
      setStep('city');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Loading gate for edit mode ────────────────────────────────────────────

  if (isEditMode && isLoadingExisting) {
    return (
      <div className={styles.loadingPage}>
        <Spinner size="lg" />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wizard}>
      <div className={styles.stepContent}>
        {step === 'city' && (
          <SelectCity
            selected={formData.city}
            onSelect={(city) => {
              updateForm('city', city);
              setStep('theme');
            }}
          />
        )}

        {step === 'theme' && (
          <SelectThemeStep
            selected={formData.theme_slugs}
            onConfirm={(slugs) => {
              updateForm('theme_slugs', slugs);
              setStep('details');
            }}
            onBack={() => setStep('city')}
            title="Select Event Themes"
            subtitle="Choose one or more themes for your invitation"
          />
        )}

        {step === 'details' && (
          <EventDetailsStep
            formData={formData}
            updateForm={updateForm}
            onNext={() => setStep('images')}
            onBack={() => setStep('theme')}
          />
        )}

        {step === 'images' && (
          <InvitationImageStep
            formData={formData}
            onBack={() => setStep('details')}
          />
        )}
      </div>
    </div>
  );
};
